/**
 * FDACredibilityResultsView - FDA AI Credibility Assessment Results Page
 * ============================================================================
 * DC-934: Integrate FDA components into Journey Dashboard
 *
 * Displays:
 * - Journey header with FDA credibility status
 * - FitForUseIndicator for primary determination
 * - CredibilityGapsPanel with filtering by severity
 * - Export options (PDF/JSON)
 * - Navigation back to Strategic dashboard
 *
 * Pattern: Follows ComplianceResultsView.tsx (DC-821)
 *
 * @version 1.0.0
 * @created 2026-01-10
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { FitForUseIndicator } from '../../components/lenses/FitForUseIndicator';
import { CredibilityGapsPanel, FDACredibilityGap } from '../../components/lenses/CredibilityGapsPanel';
import {
  useFDACredibility,
  getCredibilityLevelColor,
  getCredibilityLevelDescription,
} from '../../components/lenses/useFDACredibility';
import '../../styles/journey.css';

// ============================================================================
// Types
// ============================================================================

interface JourneyInfo {
  group_id: string;
  name: string;
  description?: string;
}

interface GapsApiResponse {
  success: boolean;
  count: number;
  summary: {
    critical: number;
    major: number;
    minor: number;
    observation: number;
    total: number;
  };
  gaps: FDACredibilityGap[];
}

// ============================================================================
// Main Component
// ============================================================================

export const FDACredibilityResultsView: React.FC = () => {
  const { group_id } = useParams<{ group_id: string }>();
  const navigate = useNavigate();
  const { authFetch } = useApiClient();

  // Journey info
  const [journey, setJourney] = useState<JourneyInfo | null>(null);
  const [journeyLoading, setJourneyLoading] = useState(true);
  const [journeyError, setJourneyError] = useState<string | null>(null);

  // FDA Credibility data via hook
  const {
    result,
    loading: resultLoading,
    isRunning,
    runAssessment,
  } = useFDACredibility(group_id || '', { autoFetch: true });

  // Gaps data (fetched separately for full detail)
  const [gaps, setGaps] = useState<FDACredibilityGap[]>([]);
  const [gapsLoading, setGapsLoading] = useState(false);

  // Export state
  const [exporting, setExporting] = useState<'pdf' | 'json' | null>(null);

  // Fetch journey info
  useEffect(() => {
    if (!group_id) return;

    const fetchJourney = async () => {
      try {
        setJourneyLoading(true);
        setJourneyError(null);

        const response = await authFetch(`/api/journey/${group_id}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch journey: ${response.statusText}`);
        }

        const data = await response.json();
        setJourney(data.journey || data);
      } catch (err: unknown) {
        console.error('[FDACredibilityResultsView] Journey fetch error:', err);
        setJourneyError(err instanceof Error ? err.message : 'Failed to load journey');
      } finally {
        setJourneyLoading(false);
      }
    };

    fetchJourney();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch when group_id changes
  }, [group_id]);

  // Fetch gaps when result is available
  useEffect(() => {
    if (!group_id || !result) return;

    const fetchGaps = async () => {
      try {
        setGapsLoading(true);

        const response = await fetch(`/api/v1/lenses/fda-credibility/${group_id}/gaps`);

        if (!response.ok) {
          console.warn('[FDACredibilityResultsView] Failed to fetch gaps:', response.statusText);
          // Use gaps from result as fallback
          setGaps((result as { gaps?: FDACredibilityGap[] }).gaps || []);
          return;
        }

        const data: GapsApiResponse = await response.json();
        if (data.success && data.gaps) {
          setGaps(data.gaps);
        }
      } catch (err) {
        console.error('[FDACredibilityResultsView] Gaps fetch error:', err);
        // Use gaps from result as fallback
        setGaps((result as { gaps?: FDACredibilityGap[] }).gaps || []);
      } finally {
        setGapsLoading(false);
      }
    };

    fetchGaps();
  }, [group_id, result]);

  // Handle PDF export
  const handleExportPdf = useCallback(async () => {
    if (!group_id || exporting) return;

    try {
      setExporting('pdf');

      const response = await fetch(`/api/v1/lenses/fda-credibility/${group_id}/export/pdf`);

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fda-credibility-${group_id}-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('[FDACredibilityResultsView] PDF export error:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(null);
    }
  }, [group_id, exporting]);

  // Handle JSON export
  const handleExportJson = useCallback(async () => {
    if (!group_id || exporting) return;

    try {
      setExporting('json');

      const response = await fetch(`/api/v1/lenses/fda-credibility/${group_id}/export/json`);

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fda-credibility-${group_id}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('[FDACredibilityResultsView] JSON export error:', err);
      alert('Failed to export JSON. Please try again.');
    } finally {
      setExporting(null);
    }
  }, [group_id, exporting]);

  // Handle run assessment
  const handleRunAssessment = useCallback(async () => {
    if (isRunning) return;
    await runAssessment();
  }, [isRunning, runAssessment]);

  // Handle gaps export (for CredibilityGapsPanel)
  const handleGapsExport = useCallback((format: 'csv' | 'json') => {
    // The CredibilityGapsPanel has built-in export functionality
    // This is called when user clicks export within the panel
    console.log('[FDACredibilityResultsView] Gaps export:', format);
  }, []);

  // Loading state
  const loading = journeyLoading || resultLoading;

  if (loading) {
    return (
      <div className="fda-results-view">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading FDA Credibility Assessment...</p>
        </div>
        <style>{viewStyles}</style>
      </div>
    );
  }

  // Error state
  if (journeyError || !journey) {
    return (
      <div className="fda-results-view">
        <div className="error-state">
          <div className="error-icon">!</div>
          <h3>Failed to Load Assessment</h3>
          <p>{journeyError || 'Journey not found'}</p>
          <button className="btn btn-secondary" onClick={() => navigate('/journey')}>
            Back to Dashboard
          </button>
        </div>
        <style>{viewStyles}</style>
      </div>
    );
  }

  // No assessment state
  if (!result) {
    return (
      <div className="fda-results-view">
        <div className="fda-results-header">
          <div className="header-breadcrumb">
            <button className="breadcrumb-link" onClick={() => navigate('/journey')}>
              Journeys
            </button>
            <span className="breadcrumb-separator">/</span>
            <button className="breadcrumb-link" onClick={() => navigate(`/journey/${group_id}`)}>
              {journey.name}
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">FDA Credibility</span>
          </div>
        </div>
        <div className="empty-state">
          <div className="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
          </div>
          <h3>No FDA Credibility Assessment</h3>
          <p>Run an FDA Credibility assessment to evaluate data fitness for regulatory AI/ML use.</p>
          <button
            className="btn btn-primary"
            onClick={handleRunAssessment}
            disabled={isRunning}
          >
            {isRunning ? 'Running Assessment...' : 'Run Assessment'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/journey/${group_id}`)}
            style={{ marginLeft: '0.75rem' }}
          >
            Back to Strategic View
          </button>
        </div>
        <style>{viewStyles}</style>
      </div>
    );
  }

  // The per-step sub-scores (Relevance: Context of Use / Representativeness /
  // Sample Size; Reliability: Accuracy / Completeness / Traceability) now flow
  // from the lens result (S2). Build the breakdown from the REAL values — no
  // fabrication; a missing step scores 0 and reads as a fail, never a fake pass.
  const ffu = result.fit_for_use;
  const sub = (s?: { score: number }) => {
    const score = s?.score ?? 0;
    return { score, passed: score >= 70 };
  };
  const relevanceSubScores = ffu?.relevance
    ? {
        contextOfUse: sub(ffu.relevance.context_of_use),
        representativeness: sub(ffu.relevance.representativeness),
        sampleSize: sub(ffu.relevance.sample_size),
      }
    : undefined;
  const reliabilitySubScores = ffu?.reliability
    ? {
        accuracy: sub(ffu.reliability.accuracy),
        completeness: sub(ffu.reliability.completeness),
        traceability: sub(ffu.reliability.traceability),
      }
    : undefined;

  // Compute gaps count
  const gapsCount = result.gaps_summary?.total ?? gaps.length;

  return (
    <div className="fda-results-view">
      {/* Header */}
      <div className="fda-results-header">
        <div className="header-breadcrumb">
          <button className="breadcrumb-link" onClick={() => navigate('/journey')}>
            Journeys
          </button>
          <span className="breadcrumb-separator">/</span>
          <button className="breadcrumb-link" onClick={() => navigate(`/journey/${group_id}`)}>
            {journey.name}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">FDA Credibility</span>
        </div>

        <div className="header-title-row">
          <div className="header-left">
            <h1>FDA AI Credibility Assessment</h1>
            <p className="subtitle">
              Data &amp; context credibility, aligned to FDA&rsquo;s risk-based AI credibility framework
              (Jan 2025 draft guidance; RWD Fit-for-Use basis)
            </p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-outline"
              onClick={handleExportJson}
              disabled={exporting !== null}
            >
              {exporting === 'json' ? 'Exporting...' : 'Export JSON'}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleExportPdf}
              disabled={exporting !== null}
            >
              {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Context of Use — the frame the verdict is judged against (S1) */}
      <div className={`fda-cou ${result.context_of_use?.declared ? '' : 'fda-cou--undeclared'}`}>
        <div className="fda-cou-label">Context of Use</div>
        {result.context_of_use?.declared ? (
          <div className="fda-cou-body">
            {result.context_of_use.problem_statement && (
              <p className="fda-cou-statement">{result.context_of_use.problem_statement}</p>
            )}
            <div className="fda-cou-meta">
              {result.context_of_use.intended_use && (
                <span><b>Intended use:</b> {result.context_of_use.intended_use}</span>
              )}
              {result.context_of_use.problem_type && (
                <span><b>Task:</b> {result.context_of_use.problem_type}</span>
              )}
              {result.context_of_use.sensitivity_level && (
                <span><b>Sensitivity:</b> {result.context_of_use.sensitivity_level}</span>
              )}
            </div>
          </div>
        ) : (
          <div className="fda-cou-body">
            <p className="fda-cou-undeclared-msg">
              No Context of Use declared. FDA credibility is judged <em>for a specific context</em> — declare
              the question and intended use in Contextual Validity so this is a context-conditional verdict,
              not an abstract score.
            </p>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/journey/${group_id}?tab=contextual-validity`)}
            >
              Declare Context of Use
            </button>
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="fda-results-content">
        {/* Sidebar */}
        <aside className="fda-sidebar">
          {/* Overall Score Card */}
          <div className="summary-card">
            <h3>Overall Credibility</h3>
            <div className="score-display">
              <div
                className="score-value"
                style={{ color: getCredibilityLevelColor(result.credibility_level) }}
              >
                {Math.round(result.overall_score)}
              </div>
              <div className="score-label">/100</div>
            </div>
            <div
              className="credibility-level"
              style={{ background: getCredibilityLevelColor(result.credibility_level) }}
            >
              {result.credibility_level.toUpperCase()}
            </div>
            <p className="credibility-description">
              {getCredibilityLevelDescription(result.credibility_level)}
            </p>
          </div>

          {/* Dimension Scores */}
          {(result.relevance_score !== undefined || result.reliability_score !== undefined) && (
            <div className="dimensions-card">
              <h3>Dimension Scores</h3>
              {result.relevance_score !== undefined && (
                <div className="dimension-row">
                  <span className="dimension-label">Relevance</span>
                  <span className="dimension-score">{Math.round(result.relevance_score)}%</span>
                  <div className="dimension-bar">
                    <div
                      className="dimension-bar-fill"
                      style={{
                        width: `${result.relevance_score}%`,
                        background: result.relevance_score >= 70 ? 'var(--tri-good)' : 'var(--tri-hard)',
                      }}
                    />
                  </div>
                </div>
              )}
              {result.reliability_score !== undefined && (
                <div className="dimension-row">
                  <span className="dimension-label">Reliability</span>
                  <span className="dimension-score">{Math.round(result.reliability_score)}%</span>
                  <div className="dimension-bar">
                    <div
                      className="dimension-bar-fill"
                      style={{
                        width: `${result.reliability_score}%`,
                        background: result.reliability_score >= 70 ? 'var(--tri-good)' : 'var(--tri-hard)',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Model Risk Card */}
          {result.model_risk && (
            <div className="risk-card">
              <h3>Model Risk</h3>
              <div className="risk-level" data-level={result.model_risk.calculated_risk}>
                {result.model_risk.calculated_risk.toUpperCase()} RISK
              </div>
              <div className="risk-details">
                <div className="risk-detail-row">
                  <span>Influence:</span>
                  <span>{result.model_risk.influence}</span>
                </div>
                <div className="risk-detail-row">
                  <span>Consequence:</span>
                  <span>{result.model_risk.consequence}</span>
                </div>
                <div className="risk-detail-row">
                  <span>Evidence Required:</span>
                  <span>{result.model_risk.required_evidence_level}</span>
                </div>
              </div>
            </div>
          )}

          {/* Gaps Summary */}
          <div className="gaps-summary-card">
            <h3>Gaps Summary</h3>
            <div className="gaps-counts">
              <div className="gap-count" data-severity="critical">
                <span className="count-value">{result.gaps_summary?.critical ?? 0}</span>
                <span className="count-label">Critical</span>
              </div>
              <div className="gap-count" data-severity="major">
                <span className="count-value">{result.gaps_summary?.major ?? 0}</span>
                <span className="count-label">Major</span>
              </div>
              <div className="gap-count" data-severity="minor">
                <span className="count-value">{result.gaps_summary?.minor ?? 0}</span>
                <span className="count-label">Minor</span>
              </div>
              <div className="gap-count" data-severity="observation">
                <span className="count-value">{result.gaps_summary?.observation ?? 0}</span>
                <span className="count-label">Obs</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="fda-main">
          {/* Fit-for-Use Indicator */}
          <div className="fit-for-use-section">
            <FitForUseIndicator
              relevanceScore={result.relevance_score}
              reliabilityScore={result.reliability_score}
              overallScore={result.overall_score}
              relevanceSubScores={relevanceSubScores}
              reliabilitySubScores={reliabilitySubScores}
              isFitForUse={result.is_fit_for_use}
              credibilityLevel={result.credibility_level}
              gapsCount={gapsCount}
              requiredEvidenceLevel={result.model_risk?.required_evidence_level}
              onViewGaps={() => {
                // Scroll to gaps panel
                document.querySelector('.gaps-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
              onExportReport={handleExportPdf}
              showSubDimensions={true}
            />
          </div>

          {/* Credibility Gaps Panel */}
          <div className="gaps-section">
            {gapsLoading ? (
              <div className="gaps-loading">
                <div className="spinner-small" />
                <span>Loading gaps...</span>
              </div>
            ) : (
              <CredibilityGapsPanel
                gaps={gaps}
                onExport={handleGapsExport}
                showSummaryBar={true}
                showFilters={true}
                autoExpandSeverity={['critical', 'major']}
                maxHeight="600px"
              />
            )}
          </div>
        </main>
      </div>

      {/* Footer */}
      <div className="fda-results-footer">
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/journey/${group_id}`)}
        >
          Back to Strategic View
        </button>
        <div className="footer-meta">
          <span>Assessed: {result.assessed_at ? new Date(result.assessed_at).toLocaleString() : 'N/A'}</span>
          <span className="separator">|</span>
          <span>Version: {result.assessor_version || '1.0.0'}</span>
        </div>
      </div>

      <style>{viewStyles}</style>
    </div>
  );
};

// ============================================================================
// Styles
// ============================================================================

const viewStyles = `
  .fda-results-view {
    min-height: 100vh;
    background: var(--tri-cream);
    font-family: var(--tri-font);
    padding: 1.5rem;
    color: var(--tri-body);
  }

  .fda-results-header {
    margin-bottom: 1.5rem;
  }

  .header-breadcrumb {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--tri-body);
    margin-bottom: 1rem;
  }

  .breadcrumb-link {
    background: none;
    border: none;
    color: var(--tri-teal);
    cursor: pointer;
    padding: 0;
    font-size: 0.875rem;
  }

  .breadcrumb-link:hover {
    text-decoration: underline;
  }

  .breadcrumb-separator {
    color: var(--tri-taupe);
  }

  .breadcrumb-current {
    color: var(--tri-ink);
  }

  .header-title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .header-left h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--tri-teal);
    margin: 0 0 0.25rem;
  }

  .header-left .subtitle {
    font-size: 0.95rem;
    color: var(--tri-body);
    margin: 0;
    max-width: 720px;
  }

  .header-actions {
    display: flex;
    gap: 0.75rem;
  }

  /* Context of Use frame (S1) */
  .fda-cou {
    background: var(--tri-card);
    border: 1px solid var(--tri-taupe);
    border-left: 3px solid var(--tri-teal);
    border-radius: var(--tri-radius);
    box-shadow: var(--tri-shadow);
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
  }
  .fda-cou--undeclared { border-left-color: var(--tri-soft-dk); }
  .fda-cou-label {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--tri-terra);
    margin-bottom: 0.5rem;
  }
  .fda-cou-statement {
    font-size: 1rem;
    color: var(--tri-ink);
    margin: 0 0 0.5rem;
    line-height: 1.5;
  }
  .fda-cou-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    font-size: 0.8125rem;
    color: var(--tri-body);
    text-transform: capitalize;
  }
  .fda-cou-meta b { color: var(--tri-ink); font-weight: 600; }
  .fda-cou-undeclared-msg {
    font-size: 0.875rem;
    color: var(--tri-body);
    margin: 0 0 0.75rem;
    line-height: 1.5;
  }

  .fda-results-content {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 1024px) {
    .fda-results-content {
      grid-template-columns: 1fr;
    }
  }

  .fda-sidebar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .summary-card,
  .dimensions-card,
  .risk-card,
  .gaps-summary-card {
    background: var(--tri-card);
    border: 1px solid var(--tri-taupe);
    border-top: 3px solid var(--tri-teal);
    border-radius: var(--tri-radius);
    box-shadow: var(--tri-shadow);
    padding: 1.25rem;
  }

  .summary-card h3,
  .dimensions-card h3,
  .risk-card h3,
  .gaps-summary-card h3 {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--tri-body);
    margin: 0 0 1rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .score-display {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.25rem;
    margin-bottom: 1rem;
  }

  .score-value {
    font-size: 3rem;
    font-weight: 700;
  }

  .score-label {
    font-size: 1.25rem;
    color: var(--tri-body);
  }

  .credibility-level {
    text-align: center;
    padding: 0.5rem 1rem;
    border-radius: var(--tri-radius-sm);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--tri-on-teal);
    margin-bottom: 0.75rem;
  }

  .credibility-description {
    font-size: 0.8125rem;
    color: var(--tri-body);
    text-align: center;
    margin: 0;
    line-height: 1.4;
  }

  .dimension-row {
    margin-bottom: 1rem;
  }

  .dimension-row:last-child {
    margin-bottom: 0;
  }

  .dimension-row .dimension-label {
    font-size: 0.875rem;
    color: var(--tri-ink);
  }

  .dimension-row .dimension-score {
    float: right;
    font-weight: 600;
    color: var(--tri-body);
  }

  .dimension-bar {
    height: 6px;
    background: var(--tri-track);
    border-radius: 3px;
    overflow: hidden;
    margin-top: 0.5rem;
  }

  .dimension-bar-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s ease;
  }

  .risk-level {
    text-align: center;
    padding: 0.5rem 1rem;
    border-radius: var(--tri-radius-sm);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--tri-on-teal);
    margin-bottom: 1rem;
  }

  .risk-level[data-level="low"] {
    background: var(--tri-good);
  }

  .risk-level[data-level="medium"] {
    background: var(--tri-soft-dk);
  }

  .risk-level[data-level="high"] {
    background: var(--tri-hard);
  }

  .risk-details {
    font-size: 0.8125rem;
  }

  .risk-detail-row {
    display: flex;
    justify-content: space-between;
    padding: 0.375rem 0;
    border-bottom: 1px solid var(--tri-taupe);
  }

  .risk-detail-row:last-child {
    border-bottom: none;
  }

  .risk-detail-row span:first-child {
    color: var(--tri-body);
  }

  .risk-detail-row span:last-child {
    color: var(--tri-ink);
    text-transform: capitalize;
  }

  .gaps-counts {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .gap-count {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.75rem;
    background: var(--tri-cream);
    border: 1px solid var(--tri-taupe);
    border-radius: var(--tri-radius-sm);
  }

  .gap-count[data-severity="critical"] .count-value { color: var(--tri-hard); }
  .gap-count[data-severity="major"] .count-value { color: var(--tri-terra); }
  .gap-count[data-severity="minor"] .count-value { color: var(--tri-soft-dk); }
  .gap-count[data-severity="observation"] .count-value { color: var(--tri-teal); }

  .count-value {
    font-size: 1.5rem;
    font-weight: 700;
  }

  .count-label {
    font-size: 0.75rem;
    color: var(--tri-body);
  }

  .fda-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .fit-for-use-section {
    background: var(--tri-card);
    border: 1px solid var(--tri-taupe);
    border-radius: var(--tri-radius);
    box-shadow: var(--tri-shadow);
    padding: 1.25rem;
  }

  .gaps-section {
    background: var(--tri-card);
    border: 1px solid var(--tri-taupe);
    border-radius: var(--tri-radius);
    box-shadow: var(--tri-shadow);
    padding: 1.25rem;
  }

  .gaps-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2rem;
    color: var(--tri-body);
  }

  .spinner-small {
    width: 20px;
    height: 20px;
    border: 2px solid var(--tri-taupe);
    border-top-color: var(--tri-teal);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .fda-results-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1rem;
    border-top: 1px solid var(--tri-taupe);
  }

  .footer-meta {
    font-size: 0.8125rem;
    color: var(--tri-body);
    display: flex;
    gap: 0.75rem;
  }

  .footer-meta .separator {
    color: var(--tri-taupe);
  }

  /* Button styles */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border-radius: var(--tri-radius-sm);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    border: none;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background: var(--tri-teal);
    color: var(--tri-on-teal);
  }

  .btn-primary:hover:not(:disabled) {
    filter: brightness(0.93);
  }

  .btn-secondary {
    background: var(--tri-teal-tint);
    color: var(--tri-teal);
    border: 1px solid var(--tri-teal-tint-line);
  }

  .btn-secondary:hover:not(:disabled) {
    filter: brightness(0.97);
  }

  .btn-outline {
    background: transparent;
    color: var(--tri-body);
    border: 1px solid var(--tri-taupe);
  }

  .btn-outline:hover:not(:disabled) {
    background: var(--tri-cream);
    color: var(--tri-ink);
  }

  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
  }

  .error-icon {
    width: 64px;
    height: 64px;
    background: var(--tri-hard);
    color: var(--tri-on-teal);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }

  .empty-icon {
    width: 80px;
    height: 80px;
    color: var(--tri-body);
    margin-bottom: 1rem;
  }

  .empty-icon svg {
    width: 100%;
    height: 100%;
  }

  .empty-state h3,
  .error-state h3 {
    font-size: 1.25rem;
    color: var(--tri-ink);
    margin: 0 0 0.5rem;
  }

  .empty-state p,
  .error-state p {
    color: var(--tri-body);
    margin: 0 0 1.5rem;
    max-width: 400px;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--tri-taupe);
    border-top-color: var(--tri-teal);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default FDACredibilityResultsView;
